import { useMemo } from 'react';

import { isRole } from '@studio/access';
import type { LoginMode } from '@studio/api-custom-attributes';
import {
  extractPublishingSite,
  isPublishingSiteRole,
} from '@studio/api-identity';
import type { CurrentUser } from '@studio/api-users';
import { createExperienceRoleResolver } from '@studio/core/utils/experience/roles';
import type { PermissionGroups } from '@studio/core/utils/permission';

import { useExperienceContext } from '../ExperienceManager/context';
import { useSessionState } from './contexts';
import {
  selectLoginMode,
  selectPermissionGroups,
  selectRealm,
  selectUser,
} from './selectors';

export { useSessionState };

// If we could pass a selector to useContext, we could use our selectors instead
// https://github.com/reactjs/rfcs/pull/119

export function useUser(): CurrentUser | undefined {
  const sessionState = useSessionState();
  const user = selectUser(sessionState);
  const permissionGroups = selectPermissionGroups(sessionState);
  const experienceContext = useExperienceContext();

  return useMemo(() => {
    if (
      user == null ||
      permissionGroups == null ||
      experienceContext?.experience == null
    ) {
      return user;
    }

    const resolveRoles = createExperienceRoleResolver(
      user.legacyRoles,
      user.legacyRoleConditions,
      user.groups,
      permissionGroups,
    );

    const resolvedRoles = resolveRoles(experienceContext.experience);

    return {
      ...user,
      roles: resolvedRoles.values().filter(isRole).toArray(),
      allowedPublishingSites: resolvedRoles
        .values()
        .filter(isPublishingSiteRole)
        .map(extractPublishingSite)
        .toArray(),
    };
  }, [experienceContext, permissionGroups, user]);
}

export function useUserRaw(): CurrentUser | undefined {
  return selectUser(useSessionState());
}

export function useLoginMode(): LoginMode {
  return selectLoginMode(useSessionState());
}

export function useRealm(): string | undefined {
  return selectRealm(useSessionState());
}

export function usePermissionGroups(): PermissionGroups | undefined {
  return selectPermissionGroups(useSessionState());
}
